/**
 * 점검 등록/수정 모달
 */
import React, { useState, useEffect } from 'react';
import { Maintenance, CreateMaintenanceRequestDto, UpdateMaintenanceRequestDto } from '../types/maintenance.types';
import { CommonCodeDto } from '@/shared/types/commonCode.types';
import { format } from 'date-fns';
import '../styles/MaintenanceModal.css';

interface MaintenanceModalProps {
    maintenance: Maintenance | null;
    msgCodes: CommonCodeDto[];
    onSave: (data: CreateMaintenanceRequestDto | UpdateMaintenanceRequestDto) => void;
    onClose: () => void;
    loading: boolean;
}

export const MaintenanceModal: React.FC<MaintenanceModalProps> = ({
                                                                      maintenance,
                                                                      msgCodes,
                                                                      onSave,
                                                                      onClose,
                                                                      loading
                                                                  }) => {
    const isEdit = !!maintenance;

    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        msgCode: 'MSG001',
        msgContent: '',
        useYn: 'Y'
    });

    // 수정 모드일 때 기존 데이터 로드
    useEffect(() => {
        if (maintenance) {
            setFormData({
                startDate: format(maintenance.startDate, "yyyy-MM-dd'T'HH:mm"),
                endDate: format(maintenance.endDate, "yyyy-MM-dd'T'HH:mm"),
                msgCode: maintenance.msgCode,
                msgContent: maintenance.msgContent || '',
                useYn: maintenance.useYn
            });
        }
    }, [maintenance]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.startDate || !formData.endDate) {
            alert('점검 시작/종료 일시를 입력해주세요.');
            return;
        }

        if (new Date(formData.startDate) >= new Date(formData.endDate)) {
            alert('종료 일시는 시작 일시보다 이후여야 합니다.');
            return;
        }

        if (formData.msgCode === 'CUSTM' && !formData.msgContent.trim()) {
            alert('직접 입력 시 점검 문구를 입력해주세요.');
            return;
        }

        const data = {
            ...(isEdit && { seqNo: maintenance!.seqNo }),
            startDate: formData.startDate,
            endDate: formData.endDate,
            msgCode: formData.msgCode,
            msgContent: formData.msgCode === 'CUSTM' ? formData.msgContent : undefined,
            useYn: formData.useYn
        };

        onSave(data);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{isEdit ? '점검 수정' : '점검 등록'}</h3>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>점검 시작 일시 <span className="required">*</span></label>
                            <input
                                type="datetime-local"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>점검 종료 일시 <span className="required">*</span></label>
                            <input
                                type="datetime-local"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>점검 문구 <span className="required">*</span></label>
                            <select
                                name="msgCode"
                                value={formData.msgCode}
                                onChange={handleChange}
                            >
                                {msgCodes.map(code => (
                                    <option key={code.comCode} value={code.comCode}>
                                        {code.comCdName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {formData.msgCode === 'CUSTM' && (
                            <div className="form-group">
                                <label>직접 입력 <span className="required">*</span></label>
                                <textarea
                                    name="msgContent"
                                    value={formData.msgContent}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="점검 안내 문구를 입력해주세요."
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label>활성화 여부</label>
                            <select
                                name="useYn"
                                value={formData.useYn}
                                onChange={handleChange}
                            >
                                <option value="Y">활성화</option>
                                <option value="N">비활성화</option>
                            </select>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            취소
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? '처리중...' : (isEdit ? '수정' : '등록')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
